import type {
  ConsistencyViolation,
  EventView,
  FolderDetail,
  FolderSummary,
  LabelBatchDetail,
  LabelBatchSummary,
  LocationType,
  LocationView,
  Paginated,
  CompanyView,
  PrinterView,
  PrintLocationRangeResult,
  EffectivePrinterView,
  PrintNodePrinterView,
  ReparseSpineResult,
  SpineIgnorePatternView,
  UpdateSpineFieldsInput,
} from '@persped/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, newIdempotencyKey } from './client';

/**
 * A lekérdezés-kulcsok EGY helyen. Egy elgépelt kulcs nem hibát okoz, hanem
 * néma cache-hibát: a mutáció után a lista nem frissülne, és a felhasználó
 * elavult adatot látna anélkül, hogy bármi jelezné.
 */
export const queryKeys = {
  labelBatches: (query: LabelBatchListQuery) => ['label-batches', query] as const,
  labelBatch: (id: string) => ['label-batches', id] as const,
  locations: (query: LocationListQuery) => ['locations', query] as const,
  location: (barcode: string) => ['locations', barcode] as const,
  locationFolders: (barcode: string) => ['locations', barcode, 'folders'] as const,
  events: (query: EventListQuery) => ['events', query] as const,
  consistency: () => ['admin', 'consistency-check'] as const,
  companies: () => ['companies'] as const,
  spineIgnorePatterns: () => ['spine-ignore-patterns'] as const,
  printers: () => ['printers'] as const,
  availablePrinters: () => ['printers', 'available'] as const,
  effectivePrinter: () => ['printers', 'effective'] as const,
  folders: (query: FolderListQuery) => ['folders', query] as const,
  folder: (barcode: string) => ['folders', barcode] as const,
  folderHistory: (barcode: string) => ['folders', barcode, 'history'] as const,
  palletHistory: (barcode: string) => ['pallets', barcode, 'history'] as const,
};

// --- Címkekötegek -----------------------------------------------------------

export interface LabelBatchListQuery {
  printStatus?: string;
  labelKind?: string;
  includeVoided?: boolean;
  page: number;
  pageSize: number;
}

export function useLabelBatches(query: LabelBatchListQuery) {
  return useQuery({
    queryKey: queryKeys.labelBatches(query),
    queryFn: ({ signal }) =>
      apiFetch<Paginated<LabelBatchSummary>>('/label-batches', {
        signal,
        query: {
          printStatus: query.printStatus,
          labelKind: query.labelKind,
          includeVoided: query.includeVoided,
          page: query.page,
          pageSize: query.pageSize,
        },
      }),
  });
}

export function useLabelBatch(id: string) {
  return useQuery({
    queryKey: queryKeys.labelBatch(id),
    queryFn: ({ signal }) => apiFetch<LabelBatchDetail>(`/label-batches/${id}`, { signal }),
  });
}

/**
 * Minden köteg-mutáció ugyanazt a mintát követi: az idempotencia-kulcs a
 * `mutationFn` hívásakor születik (tehát felhasználói műveletenként egy),
 * és siker után az ÖSSZES köteg-lekérdezést érvénytelenítjük — a lista
 * `printStatus` oszlopa és a részletek is elavulhattak.
 */
function useBatchMutation<TInput>(
  path: (input: TInput) => string,
  body: (input: TInput) => object,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) =>
      apiFetch<LabelBatchDetail>(path(input), {
        method: 'POST',
        body: body(input),
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['label-batches'] }),
  });
}

export const usePrintFolderLabels = () =>
  useBatchMutation<{ count: number; note?: string }>(
    () => '/label-batches/folders',
    (i) => ({ count: i.count, note: i.note || undefined }),
  );

export const usePrintLocationLabels = () =>
  useBatchMutation<{ barcodes: string[]; note?: string }>(
    () => '/label-batches/locations',
    (i) => ({ barcodes: i.barcodes, note: i.note || undefined }),
  );

export const useAllocateLabels = () =>
  useBatchMutation<{ fromSequence: number; toSequence: number; reason: string }>(
    () => '/labels/allocations',
    (i) => ({ fromSequence: i.fromSequence, toSequence: i.toSequence, reason: i.reason }),
  );

export const useResendBatch = () =>
  useBatchMutation<{ id: string; reason: string; fromBarcode?: string; toBarcode?: string }>(
    (i) => `/label-batches/${i.id}/resend`,
    (i) => ({
      reason: i.reason,
      fromBarcode: i.fromBarcode || undefined,
      toBarcode: i.toBarcode || undefined,
    }),
  );

export const useConfirmBatch = () =>
  useBatchMutation<{ id: string }>((i) => `/label-batches/${i.id}/confirm`, () => ({}));

export const useVoidBatch = () =>
  useBatchMutation<{ id: string; reason: string }>(
    (i) => `/label-batches/${i.id}/void`,
    (i) => ({ reason: i.reason }),
  );

// --- Lokációk ---------------------------------------------------------------

export interface LocationListQuery {
  locationType?: string;
  status?: string;
  query?: string;
  /** F5 (08-13 CR 8.1) — a szerver rendez, nem a kliens. */
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface LocationFolderRow {
  barcode: string;
  currentText: string | null;
  palletLevel: number | null;
  since: string;
}

export function useLocations(query: LocationListQuery) {
  return useQuery({
    // A végpont NEM lapoz — a teljes listát adja vissza, a szűrést és a
    // megjelenítést itt végezzük.
    queryKey: queryKeys.locations(query),
    queryFn: ({ signal }) =>
      apiFetch<LocationView[]>('/locations', {
        signal,
        query: {
          locationType: query.locationType,
          status: query.status,
          query: query.query,
          sortBy: query.sortBy,
          sortDir: query.sortDir,
        },
      }),
  });
}

export function useLocation(barcode: string) {
  return useQuery({
    queryKey: queryKeys.location(barcode),
    queryFn: ({ signal }) => apiFetch<LocationView>(`/locations/${barcode}`, { signal }),
  });
}

export function useLocationFolders(barcode: string) {
  return useQuery({
    queryKey: queryKeys.locationFolders(barcode),
    queryFn: ({ signal }) =>
      apiFetch<LocationFolderRow[]>(`/locations/${barcode}/folders`, { signal }),
  });
}

export function useCreateLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      barcode: string;
      name: string;
      locationType: string;
      /** Csak SHELF típusnál: a polc szintjeinek száma (D fázis). */
      levelCount?: number;
      notes?: string;
    }) =>
      apiFetch<LocationView>('/locations', {
        method: 'POST',
        body: {
          barcode: input.barcode,
          name: input.name,
          locationType: input.locationType,
          levelCount: input.levelCount,
          notes: input.notes || undefined,
        },
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['locations'] }),
  });
}

export function useUpdateLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      barcode: string;
      name?: string;
      notes?: string | null;
      status?: string;
    }) =>
      apiFetch<LocationView>(`/locations/${input.barcode}`, {
        method: 'PATCH',
        body: { name: input.name, notes: input.notes, status: input.status },
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['locations'] }),
  });
}

// --- Eseménynapló (spec 12. § — közös eseményforrás) --------------------------

export interface EventListQuery {
  eventType?: string;
  batchId?: string;
  folderBarcode?: string;
  palletBarcode?: string;
  from?: string;
  to?: string;
  page: number;
  pageSize: number;
}

export function useEvents(query: EventListQuery) {
  return useQuery({
    queryKey: queryKeys.events(query),
    queryFn: ({ signal }) =>
      apiFetch<Paginated<EventView>>('/events', {
        signal,
        query: {
          eventType: query.eventType,
          batchId: query.batchId,
          folderBarcode: query.folderBarcode,
          palletBarcode: query.palletBarcode,
          from: query.from,
          to: query.to,
          page: query.page,
          pageSize: query.pageSize,
        },
      }),
  });
}

/**
 * Konzisztencia-ellenőrzés. Az ÜRES lista a jó eredmény — ez a folyamatos
 * bizonyíték arra, hogy az „egy mappának pontosan egy aktív lokációja van"
 * invariáns él (spec 17.5 §).
 */
export function useConsistencyCheck() {
  return useQuery({
    queryKey: queryKeys.consistency(),
    queryFn: ({ signal }) => apiFetch<ConsistencyViolation[]>('/admin/consistency-check', { signal }),
    // Nem cache-eljük hosszan: ez diagnosztika, a friss állapot a lényeg.
    staleTime: 0,
  });
}

// --- Mappák -----------------------------------------------------------------

export interface FolderListQuery {
  query?: string;
  status?: string;
  locationBarcode?: string;
  /** Gerincmező-szűrők (C3 befejezése, D fázis 7.1). */
  company?: string;
  documentType?: string;
  yearFrom?: number;
  yearTo?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  page: number;
  pageSize: number;
}

export function useFolders(query: FolderListQuery, enabled = true) {
  return useQuery({
    queryKey: queryKeys.folders(query),
    enabled,
    queryFn: ({ signal }) =>
      apiFetch<Paginated<FolderSummary>>('/folders', {
        signal,
        query: {
          query: query.query,
          status: query.status,
          locationBarcode: query.locationBarcode,
          company: query.company,
          documentType: query.documentType,
          yearFrom: query.yearFrom,
          yearTo: query.yearTo,
          sortBy: query.sortBy,
          sortDir: query.sortDir,
          page: query.page,
          pageSize: query.pageSize,
        },
      }),
  });
}

export function useFolder(barcode: string) {
  return useQuery({
    queryKey: queryKeys.folder(barcode),
    queryFn: ({ signal }) => apiFetch<FolderDetail>(`/folders/${barcode}`, { signal }),
  });
}

/**
 * A mappa előzményei. A végpont a 200 legutóbbi eseményt adja, lapozás
 * nélkül — mélyebb történethez a `GET /events?folderBarcode=…` kell.
 */
export function useFolderHistory(barcode: string) {
  return useQuery({
    queryKey: queryKeys.folderHistory(barcode),
    queryFn: ({ signal }) => apiFetch<EventView[]>(`/folders/${barcode}/history`, { signal }),
  });
}

export function usePalletHistory(barcode: string) {
  return useQuery({
    queryKey: queryKeys.palletHistory(barcode),
    queryFn: ({ signal }) => apiFetch<EventView[]>(`/pallets/${barcode}/history`, { signal }),
  });
}

/**
 * Rövid életű SAS URL egy mappafotóhoz.
 *
 * Szándékosan MUTÁCIÓ, nem lekérdezés: a képek nem publikus URL-en élnek
 * (spec 17.4 §), a SAS pedig pár percig érvényes. Ha lekérdezésként
 * cache-elnénk, egy később megnyitott fülön már lejárt linket adnánk.
 * Így a URL akkor és csak akkor keletkezik, amikor a felhasználó rákattint.
 */
export function useImageUrl(barcode: string) {
  return useMutation({
    mutationFn: (imageId: string) =>
      apiFetch<{ url: string; expiresAt: string }>(`/folders/${barcode}/images/${imageId}/url`),
  });
}

// --- Írási műveletek (W3) ---------------------------------------------------

/**
 * Közös minta a mappa-mutációkhoz: az idempotencia-kulcs a hívás
 * pillanatában születik, és siker után a mappa MINDEN lekérdezését
 * érvénytelenítjük — az adatlap, az előzmény és a keresési találatok is
 * elavulhattak (pl. az áthelyezés a lokációt is átírja).
 */
function useFolderMutation<TInput extends { barcode: string }>(
  path: (input: TInput) => string,
  body: (input: TInput) => object,
  method: 'POST' | 'PUT' = 'POST',
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) =>
      apiFetch<FolderDetail>(path(input), {
        method,
        body: body(input),
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: (_data, input) => {
      void queryClient.invalidateQueries({ queryKey: ['folders'] });
      void queryClient.invalidateQueries({ queryKey: ['locations'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.folderHistory(input.barcode) });
      void queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

/**
 * Gerincszöveg módosítása (US-FOLDER-4 — a spec szerint „azonnal és később
 * is szerkeszthető", ez a klasszikus íróasztali javítási munkafolyamat).
 *
 * A `rowVersion` a BETÖLTÖTT adatlapról jön: ha közben más módosította,
 * a szerver `CONCURRENT_MODIFICATION`-t ad, és NEM ír felül némán
 * (spec 15.5 §). A felület ezt érthető magyar üzenetté fordítja.
 */
export const useUpdateFolderText = () =>
  useFolderMutation<{ barcode: string; text: string; rowVersion: number; isApproved?: boolean }>(
    (i) => `/folders/${i.barcode}/text`,
    (i) => ({ text: i.text, rowVersion: i.rowVersion, isApproved: i.isApproved }),
    'PUT',
  );

/**
 * A gerincadatok kézi felülbírálása (E4).
 *
 * Csak a MEGADOTT mezők utaznak: a hiányzó mező jelentése „ne nyúlj hozzá",
 * a `null`-é „kézzel üresre állítom". A hívó ezért a `Partial`-t szándékosan
 * hiányos objektummal tölti, nem `null`-ozott teljes alakkal.
 */
export type UpdateSpineFieldsVars = { barcode: string; rowVersion: number } & Partial<
  Omit<UpdateSpineFieldsInput, 'rowVersion' | 'sourceClient'>
>;

export const useUpdateSpineFields = () =>
  useFolderMutation<UpdateSpineFieldsVars>(
    (i) => `/folders/${i.barcode}/spine-fields`,
    ({ barcode: _barcode, ...body }) => body,
    'PUT',
  );

export const useMoveFolder = () =>
  useFolderMutation<{ barcode: string; targetLocationBarcode: string; targetPalletLevel?: number }>(
    (i) => `/folders/${i.barcode}/move`,
    (i) => ({
      targetLocationBarcode: i.targetLocationBarcode,
      targetPalletLevel: i.targetPalletLevel,
    }),
  );

/** Sikertelen Azure AI felismerés újraindítása (US-FOLDER-5 AC5). */
export const useRetryRecognition = () =>
  useFolderMutation<{ barcode: string; recognitionId: string }>(
    (i) => `/folders/${i.barcode}/recognitions/${i.recognitionId}/retry`,
    () => ({}),
  );

// --- Raklap-műveletek -------------------------------------------------------

function usePalletMutation<TInput extends { palletBarcode: string }>(
  path: (input: TInput) => string,
  body: (input: TInput) => object,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) =>
      apiFetch<unknown>(path(input), {
        method: 'POST',
        body: body(input),
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['locations'] });
      void queryClient.invalidateQueries({ queryKey: ['pallets'] });
      void queryClient.invalidateQueries({ queryKey: ['folders'] });
      void queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

/**
 * SZINTMŰVELETEK (G1) — a `/locations` útvonalon, mert a szint a polcra és a
 * raklapra egyaránt vonatkozik, és ugyanaz a szabályrendszer kezeli.
 *
 * A szint SORSZÁMÁT hozzáadásnál a szerver adja: a kliens nem választhat,
 * mert a szabály („csak a legfelső fölé, ha az nem üres") zárolás alatt dől el.
 */
function useLevelMutation<TInput extends { barcode: string; reason?: string }>(
  path: (input: TInput) => string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) =>
      apiFetch<unknown>(path(input), {
        method: 'POST',
        body: { reason: input.reason || undefined },
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['locations'] });
      void queryClient.invalidateQueries({ queryKey: ['pallets'] });
      void queryClient.invalidateQueries({ queryKey: ['folders'] });
      void queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export const useAddLevel = () =>
  useLevelMutation<{ barcode: string; reason?: string }>((i) => `/locations/${i.barcode}/levels`);

/** Csak a LEGFELSŐ, ÜRES szint — a szerver ezt zárolás alatt ellenőrzi. */
export const useRemoveLevel = () =>
  useLevelMutation<{ barcode: string; levelNumber: number; reason?: string }>(
    (i) => `/locations/${i.barcode}/levels/${i.levelNumber}/remove`,
  );

/** Polcszint lezárása — a betárolást tiltja, a kivételt nem. */
export const useLockLevel = () =>
  useLevelMutation<{ barcode: string; levelNumber: number; reason?: string }>(
    (i) => `/locations/${i.barcode}/levels/${i.levelNumber}/lock`,
  );

export const useUnlockLevel = () =>
  useLevelMutation<{ barcode: string; levelNumber: number; reason?: string }>(
    (i) => `/locations/${i.barcode}/levels/${i.levelNumber}/unlock`,
  );

/**
 * Szint felbontása. Az `expectedFolderCount` a UI-n látott darabszám —
 * ha közben változott, a szerver elutasítja. Ez fordítja a megerősítő
 * képernyőt konvencióból szerver által kikényszerített invariánssá.
 */
export const useDismantleLevel = () =>
  usePalletMutation<{
    palletBarcode: string;
    levelNumber: number;
    targetLocationBarcode: string;
    targetPalletLevel?: number;
    expectedFolderCount: number;
  }>(
    (i) => `/pallets/${i.palletBarcode}/levels/${i.levelNumber}/dismantle`,
    (i) => ({
      targetLocationBarcode: i.targetLocationBarcode,
      targetPalletLevel: i.targetPalletLevel,
      expectedFolderCount: i.expectedFolderCount,
    }),
  );

export const useDismantlePallet = () =>
  usePalletMutation<{
    palletBarcode: string;
    targetLocationBarcode: string;
    targetPalletLevel?: number;
    expectedFolderCount: number;
  }>(
    (i) => `/pallets/${i.palletBarcode}/dismantle`,
    (i) => ({
      targetLocationBarcode: i.targetLocationBarcode,
      targetPalletLevel: i.targetPalletLevel,
      expectedFolderCount: i.expectedFolderCount,
    }),
  );

// --- Mappa regisztráció + fotófeltöltés (W4) --------------------------------

export function useCreateFolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      barcode: string;
      currentText?: string;
      targetLocationBarcode?: string;
      targetPalletLevel?: number;
      idempotencyKey: string;
    }) =>
      apiFetch<FolderDetail>('/folders', {
        method: 'POST',
        body: {
          barcode: input.barcode,
          currentText: input.currentText || undefined,
          targetLocationBarcode: input.targetLocationBarcode || undefined,
          targetPalletLevel: input.targetPalletLevel,
        },
        // A kulcsot a HÍVÓ adja, mert egy sikertelen feltöltés újrapróbálásakor
        // UGYANAZT kell újraküldeni — enélkül a második kísérlet új mappát
        // hozna létre a már létrehozott mellé.
        idempotencyKey: input.idempotencyKey,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['folders'] });
      void queryClient.invalidateQueries({ queryKey: ['locations'] });
      void queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export function useUploadFolderImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      barcode: string;
      blob: Blob;
      checksum: string;
      idempotencyKey: string;
    }) => {
      const form = new FormData();
      form.append('file', input.blob, `${input.barcode}.jpg`);
      form.append('checksum', input.checksum);
      form.append('capturedAt', new Date().toISOString());
      form.append('isOriginal', 'true');
      form.append('sourceClient', 'WEB');
      return apiFetch<FolderDetail>(`/folders/${input.barcode}/images`, {
        method: 'POST',
        formData: form,
        idempotencyKey: input.idempotencyKey,
      });
    },
    onSuccess: (_d, input) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.folder(input.barcode) });
      void queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

/**
 * Sérült címke újranyomtatása egy MÁR REGISZTRÁLT mappára (B9, D fázis 5.3).
 *
 * A TC22-ről a D fázis levette ezt a funkciót, ezért a weben KELL lennie —
 * enélkül egy sérült címkéjű mappa sehonnan nem lenne újranyomtatható.
 *
 * A `confirmedImageId` és a `rowVersion` visszaküldése fordítja a
 * UI-megerősítést szerver által kikényszerített invariánssá: ha a
 * gerincszöveg közben változott, a felhasználó elavult információ alapján
 * erősített meg, és a szerver `CONCURRENT_MODIFICATION`-t ad.
 */
export function useReprintFolderLabel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      barcode: string;
      confirmedImageId: string;
      rowVersion: number;
      reason: string;
    }) =>
      apiFetch<{ barcode: string; printStatus: string; hint: string | null }>(
        `/labels/reprint/${input.barcode}`,
        {
          method: 'POST',
          body: {
            confirmedImageId: input.confirmedImageId,
            rowVersion: input.rowVersion,
            reason: input.reason,
          },
          idempotencyKey: newIdempotencyKey(),
        },
      ),
    onSuccess: (_r, input) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.folder(input.barcode) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.folderHistory(input.barcode) });
    },
  });
}

// --- Cégtörzs (F1) ----------------------------------------------------------

/**
 * A cégkatalógus.
 *
 * A `COMPANY_LABEL_HU` fordítótábla HELYÉRE lépett: a cégnév az adatbázisból
 * jön, mert a lista adminisztrálható lett. Cserébe elveszett a
 * `Record<Company, string>` fordítási idejű teljességi garanciája — ezért
 * minden megjelenítés a `nameOf()` segédet használja, ami ismeretlen kódnál
 * magát a kódot adja vissza, nem `undefined`-ot.
 */
export function useCompanies() {
  return useQuery({
    queryKey: queryKeys.companies(),
    queryFn: () => apiFetch<CompanyView[]>('/companies'),
    // A törzsadat ritkán változik, de a Törzsadatok oldalon AZONNAL látszania
    // kell a mentésnek — a mutációk invalidálják, ez csak a háttérfrissítést
    // ritkítja.
    staleTime: 5 * 60_000,
  });
}

/** Ismeretlen kódnál magát a kódot adja — sosem `undefined` a felületen. */
export function companyName(companies: CompanyView[] | undefined, code: string): string {
  return companies?.find((c) => c.code === code)?.name ?? code;
}

function useCompanyMutation<TInput>(
  path: (input: TInput) => string,
  body: (input: TInput) => object,
  method: 'POST' | 'PATCH',
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) =>
      apiFetch<CompanyView>(path(input), {
        method,
        body: body(input),
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.companies() });
      // A mappák megjelenítése cégnevet mutat — az is frissüljön.
      void queryClient.invalidateQueries({ queryKey: ['folders'] });
      void queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export const useCreateCompany = () =>
  useCompanyMutation<{ code: string; name: string; aliases: string[] }>(
    () => '/companies',
    (i) => ({ code: i.code, name: i.name, aliases: i.aliases }),
    'POST',
  );

export const useUpdateCompany = () =>
  useCompanyMutation<{
    code: string;
    name?: string;
    isActive?: boolean;
    aliases?: string[];
    rowVersion: number;
  }>(
    (i) => `/companies/${i.code}`,
    ({ code: _code, ...body }) => body,
    'PATCH',
  );

/**
 * A gerincadatok újraelemzése (F2).
 *
 * `dryRun` alapból IGAZ: előbb megmutatja, hányat érintene. Egy elgépelt
 * alias így nem sorol át némán száz mappát.
 */
export function useReparseSpine() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { dryRun: boolean }) =>
      apiFetch<ReparseSpineResult>('/admin/spine/reparse', {
        method: 'POST',
        body: { dryRun: input.dryRun },
      }),
    onSuccess: (result) => {
      if (result.dryRun) return;
      void queryClient.invalidateQueries({ queryKey: ['folders'] });
      void queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

// --- Kihagyandó gerincminták (G3, CR #3) ------------------------------------

/**
 * A cégillesztés elől kivont minták.
 *
 * A mappacímkére nyomtatott rendszernév („Persped Irattár - Mappa") a
 * gerincfotón is ott van, és enélkül a cégillesztés MINDEN mappát Perspedként
 * ismerne fel.
 */
export function useSpineIgnorePatterns() {
  return useQuery({
    queryKey: queryKeys.spineIgnorePatterns(),
    queryFn: () => apiFetch<SpineIgnorePatternView[]>('/spine-ignore-patterns'),
    staleTime: 5 * 60_000,
  });
}

function useSpineIgnoreMutation<TInput>(
  path: (input: TInput) => string,
  body: (input: TInput) => object,
  method: 'POST' | 'PATCH',
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) =>
      apiFetch<SpineIgnorePatternView>(path(input), {
        method,
        body: body(input),
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.spineIgnorePatterns() });
    },
  });
}

export const useCreateSpineIgnorePattern = () =>
  useSpineIgnoreMutation<{ pattern: string; note?: string }>(
    () => '/spine-ignore-patterns',
    (i) => ({ pattern: i.pattern, note: i.note || undefined }),
    'POST',
  );

/** A minta SZÖVEGE nem módosítható — csak a megjegyzés és az aktív állapot. */
export const useUpdateSpineIgnorePattern = () =>
  useSpineIgnoreMutation<{
    id: string;
    note?: string | null;
    isActive?: boolean;
    rowVersion: number;
  }>(
    (i) => `/spine-ignore-patterns/${i.id}`,
    ({ id: _id, ...body }) => body,
    'PATCH',
  );

// --- Nyomtató (G2) ----------------------------------------------------------

/** A felvett nyomtatók. Üres listánál a szerver a konfigra esik vissza. */
export function usePrinters() {
  return useQuery({
    queryKey: queryKeys.printers(),
    queryFn: () => apiFetch<PrinterView[]>('/labels/printers'),
    staleTime: 5 * 60_000,
  });
}

/**
 * MELYIK NYOMTATÓRA MEGY MOST a nyomtatás — és honnan tudjuk.
 *
 * Üres törzsadatnál a felületen eddig sehol nem szerepelt, melyik
 * azonosítóra megy a nyomtatás. Éles üzemben ez az első kérdés, amikor nem
 * jön ki a címke.
 */
export function useEffectivePrinter() {
  return useQuery({
    queryKey: queryKeys.effectivePrinter(),
    queryFn: () => apiFetch<EffectivePrinterView>('/labels/printers/effective'),
    staleTime: 60_000,
  });
}

/**
 * A PrintNode-fiókban ELÉRHETŐ nyomtatók.
 *
 * Ez teszi a választást listává szabad szöveg helyett: egy elgépelt
 * azonosító különben csak az első sikertelen nyomtatáskor derülne ki.
 *
 * `enabled`-del kapuzva: a PrintNode API-t hívja, tehát ne fusson le minden
 * oldalbetöltésnél — csak amikor a felhasználó tényleg nyomtatót választ.
 */
export function useAvailablePrinters(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.availablePrinters(),
    queryFn: () => apiFetch<PrintNodePrinterView[]>('/labels/printers/available'),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

function usePrinterMutation<TInput>(
  path: (input: TInput) => string,
  body: (input: TInput) => object,
  method: 'POST' | 'PATCH',
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) =>
      apiFetch<PrinterView>(path(input), {
        method,
        body: body(input),
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: () => {
      // A `['printers']` prefix az `effective`-et is érvényteleníti — a
      // „hova nyomtatunk" válasznak azonnal követnie kell a változást.
      void queryClient.invalidateQueries({ queryKey: queryKeys.printers() });
      void queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export const useCreatePrinter = () =>
  usePrinterMutation<{ printnodeId: number; name: string; isActive: boolean }>(
    () => '/labels/printers',
    (i) => ({ printnodeId: i.printnodeId, name: i.name, isActive: i.isActive }),
    'POST',
  );

export const useUpdatePrinter = () =>
  usePrinterMutation<{ id: string; name?: string; isActive?: boolean; rowVersion: number }>(
    (i) => `/labels/printers/${i.id}`,
    ({ id: _id, ...body }) => body,
    'PATCH',
  );

// --- Fotóműveletek (G4, CR #1) ----------------------------------------------

function useImageMutation<TInput extends { barcode: string }>(
  path: (input: TInput) => string,
  method: 'POST' | 'PUT',
  body: (input: TInput) => object,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) =>
      apiFetch<FolderDetail>(path(input), {
        method,
        body: body(input),
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: (_data, input) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.folder(input.barcode) });
      void queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

/**
 * Az elsődleges fotó kijelölése.
 *
 * Ez a válasz a „legyen cserélhető a fotó" kérésre: a rossz kép eltűnik a
 * szem elől, de NEM íródik felül (12. alapelv).
 */
export const useSetPrimaryImage = () =>
  useImageMutation<{ barcode: string; imageId: string }>(
    (i) => `/folders/${i.barcode}/images/${i.imageId}/primary`,
    'PUT',
    () => ({}),
  );

/** NEM elsődleges fotó törlése — kötelező indokkal (GDPR). */
export const useDeleteImage = () =>
  useImageMutation<{ barcode: string; imageId: string; reason: string }>(
    (i) => `/folders/${i.barcode}/images/${i.imageId}/delete`,
    'POST',
    (i) => ({ reason: i.reason }),
  );

/**
 * ÚJ Azure AI felismerés indítása (CR #24).
 *
 * Nem azonos a `useRetryRecognition`-nel: az egy MEGLÉVŐ sort állít vissza
 * és felülírja a korábbi eredményt. Ez új sort hoz létre, hogy a régi és az
 * új egymás mellett maradjon — enélkül nem lehetne megmondani, hogy a
 * paraméterváltás javított-e.
 */
export const useRequestNewRecognition = () =>
  useFolderMutation<{ barcode: string }>(
    (i) => `/folders/${i.barcode}/recognitions`,
    () => ({}),
  );

/** Lokációcímke-tartomány nyomtatása, a hiányzó lokációk létrehozásával (CR #23). */
export function usePrintLocationRange() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      locationType: LocationType;
      fromSequence: number;
      toSequence: number;
      namePrefix?: string;
      levelCount?: number;
    }) =>
      apiFetch<PrintLocationRangeResult>('/label-batches/locations/range', {
        method: 'POST',
        body: {
          locationType: input.locationType,
          fromSequence: input.fromSequence,
          toSequence: input.toSequence,
          namePrefix: input.namePrefix || undefined,
          levelCount: input.levelCount,
        },
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['locations'] });
      void queryClient.invalidateQueries({ queryKey: ['label-batches'] });
      void queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

/** Selejtezett sorszám visszaadása a forgalomba (CR #22). */
export function useRestoreAllocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { barcode: string; reason: string }) =>
      apiFetch<unknown>(`/labels/allocations/${input.barcode}/restore`, {
        method: 'POST',
        body: { reason: input.reason },
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['label-batches'] });
      void queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}
